import { supabase } from '@/integrations/supabase/client';

import { getLogoutEpoch } from './logout-teardown-signal';
import { PG_INSUFFICIENT_PRIVILEGE, PGRST_NO_ROWS } from './pg-error-codes';

/**
 * True when an authorization failure is the tail of a sign-out rather than a
 * policy bug.
 *
 * A read issued while authenticated can land after the token is gone. Our RLS
 * helpers (`is_task_secondary_assignee`, `user_has_active_correspondence_share`,
 * `get_correspondence_timeline`) grant EXECUTE to `authenticated` and never to
 * `anon`, so the query that succeeded a moment earlier comes back
 * `permission denied for function …` — which `console.error` then ships to
 * Sentry as an unresolved issue, transaction `/auth/login`.
 *
 * Suppressing every `42501` would hide genuine GRANT and policy bugs, so the
 * monotonic logout epoch is the discriminator: it differs only when a sign-out
 * landed between the request starting and its failure returning. A signed-in
 * user hitting a real permission bug still logs.
 *
 * Callers snapshot `getLogoutEpoch()` as the first statement of the fetch, so
 * the snapshot marks when the request left, not when the component mounted.
 */
export function isPostLogoutDenial(
  error: { readonly code?: string | null } | null | undefined,
  epochAtRequestStart: number,
): boolean {
  return error?.code === PG_INSUFFICIENT_PRIVILEGE && getLogoutEpoch() !== epochAtRequestStart;
}

/** The refusals an `anon` read of authenticated data produces; nothing else is a sign-out tail. */
const SIGNED_OUT_REFUSAL_CODES: ReadonlySet<string> = new Set([
  PG_INSUFFICIENT_PRIVILEGE,
  PGRST_NO_ROWS,
]);

/**
 * True when a failed read is the tail of a sign-out: it straddled one
 * ({@link isPostLogoutDenial}), or it is an `anon`-shaped refusal and no
 * session remains now.
 *
 * The epoch alone misses reads that start after the token is gone — sign-out
 * clears the query cache while the authenticated tree is still mounted, so its
 * observers refetch as `anon`. Those come back as permission denials or as
 * PGRST116 from `.single()` over zero RLS-visible rows, and reach Sentry with
 * no user on `/auth/login`.
 *
 * Only those two codes consult the session. Anything else — timeouts, 5xx,
 * transport failures — returns false without queueing on the auth lock
 * (`getSession()` can run a retrying token refresh under it), leaving the
 * caller's own logging policy in charge. A lookup that errors — a failed
 * refresh returns `{ session: null, error }` without signing out — or throws
 * also returns false, so a signed-in user's GRANT or policy bug still logs.
 */
export async function isSignedOutReadFailure(
  error: { readonly code?: string | null } | null | undefined,
  epochAtRequestStart: number,
): Promise<boolean> {
  if (isPostLogoutDenial(error, epochAtRequestStart)) {
    return true;
  }
  if (!SIGNED_OUT_REFUSAL_CODES.has(error?.code ?? '')) {
    return false;
  }
  try {
    const { data, error: sessionError } = await supabase.auth.getSession();
    return sessionError === null && data.session === null;
  } catch {
    return false;
  }
}
